import type { EcsConfig } from './ecsConfig';
import type { EcsExperiments, Experiment, ExperimentTreatmentType, ExperimentTreatments } from './experiments';
import { defaultExperimentTreatments, experimentConfigs } from './experiments';
import type { EcsFeatureRollouts, FeatureRollout, FeatureRolloutTreatments } from './featureRollouts';
import { defaultFeatureRolloutTreatments } from './featureRollouts';
import type { EcsSourceSnapshot } from './store';

export type LegacyEcsPersistenceErrorReason = 'invalid-payload' | 'read-failed';

export class LegacyEcsPersistenceError extends Error {
  public readonly reason: LegacyEcsPersistenceErrorReason;

  constructor(reason: LegacyEcsPersistenceErrorReason) {
    super(reason === 'read-failed' ? 'Failed to read persisted ECS state.' : 'Persisted ECS state is invalid.');
    this.name = 'LegacyEcsPersistenceError';
    this.reason = reason;
  }
}

export interface LegacyEcsPersistedFields {
  readonly ecsConfig: string;
  readonly ecsExperiments: string;
  readonly ecsFeatureRollouts: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

const isStringRecord = (value: unknown): value is Record<string, string> =>
  isRecord(value) && Object.values(value).every((recordValue) => typeof recordValue === 'string');

const parseJson = (value: string): unknown => {
  const parsedValue: unknown = JSON.parse(value);

  return parsedValue;
};

const parsePersistedSlice = (
  persistedState: Record<string, unknown>,
  field: keyof LegacyEcsPersistedFields
): unknown => {
  const serializedSlice = persistedState[field];

  if (typeof serializedSlice !== 'string') {
    throw new LegacyEcsPersistenceError('invalid-payload');
  }

  try {
    return parseJson(serializedSlice);
  } catch {
    throw new LegacyEcsPersistenceError('invalid-payload');
  }
};

const decodeEcsConfig = (value: unknown): EcsConfig => {
  if (!isRecord(value)) {
    throw new LegacyEcsPersistenceError('invalid-payload');
  }

  const configIds = value.configIds;
  const etag = value.etag;
  const hasValidEtag = etag === undefined || typeof etag === 'string';

  if (!hasValidEtag || !isStringRecord(configIds)) {
    throw new LegacyEcsPersistenceError('invalid-payload');
  }

  return {
    configIds,
    etag,
  };
};

const isExperiment = (name: string): name is Experiment =>
  Object.prototype.hasOwnProperty.call(experimentConfigs, name);

const isExperimentTreatment = <E extends Experiment>(
  experiment: E,
  value: unknown
): value is ExperimentTreatmentType<E> =>
  experimentConfigs[experiment].treatments.some((treatment) => treatment === value);

const decodeExperimentTreatments = (value: unknown): Partial<ExperimentTreatments> => {
  if (!isRecord(value)) {
    throw new LegacyEcsPersistenceError('invalid-payload');
  }

  return Object.entries(value).reduce<Partial<ExperimentTreatments>>((treatments, [name, treatment]) => {
    if (!isExperiment(name)) {
      return treatments;
    }

    if (!isExperimentTreatment(name, treatment)) {
      throw new LegacyEcsPersistenceError('invalid-payload');
    }

    return {
      ...treatments,
      [name]: treatment,
    };
  }, {});
};

const decodeEcsExperiments = (value: unknown): Pick<EcsExperiments, 'treatmentsFromService' | 'treatmentOverrides'> => {
  if (!isRecord(value)) {
    throw new LegacyEcsPersistenceError('invalid-payload');
  }

  return {
    treatmentsFromService: decodeExperimentTreatments(value.treatmentsFromService),
    treatmentOverrides: decodeExperimentTreatments(value.treatmentOverrides),
  };
};

const isFeatureRollout = (name: string): name is FeatureRollout =>
  Object.prototype.hasOwnProperty.call(defaultFeatureRolloutTreatments, name);

const decodeFeatureRolloutTreatments = (value: unknown): Partial<FeatureRolloutTreatments> => {
  if (!isRecord(value)) {
    throw new LegacyEcsPersistenceError('invalid-payload');
  }

  return Object.entries(value).reduce<Partial<FeatureRolloutTreatments>>((treatments, [name, treatment]) => {
    if (!isFeatureRollout(name)) {
      return treatments;
    }

    if (typeof treatment !== 'boolean') {
      throw new LegacyEcsPersistenceError('invalid-payload');
    }

    return {
      ...treatments,
      [name]: treatment,
    };
  }, {});
};

const decodeEcsFeatureRollouts = (
  value: unknown
): Pick<EcsFeatureRollouts, 'treatmentsFromService' | 'treatmentOverrides'> => {
  if (!isRecord(value)) {
    throw new LegacyEcsPersistenceError('invalid-payload');
  }

  return {
    treatmentsFromService: decodeFeatureRolloutTreatments(value.treatmentsFromService),
    treatmentOverrides: decodeFeatureRolloutTreatments(value.treatmentOverrides),
  };
};

export const parseLegacyEcsPersistedState = (value: unknown): EcsSourceSnapshot => {
  if (!isRecord(value)) {
    throw new LegacyEcsPersistenceError('invalid-payload');
  }

  const ecsConfig = decodeEcsConfig(parsePersistedSlice(value, 'ecsConfig'));
  const ecsExperiments = decodeEcsExperiments(parsePersistedSlice(value, 'ecsExperiments'));
  const ecsFeatureRollouts = decodeEcsFeatureRollouts(parsePersistedSlice(value, 'ecsFeatureRollouts'));

  return {
    ecsConfig,
    experimentTreatmentsFromService: ecsExperiments.treatmentsFromService,
    experimentTreatmentOverrides: ecsExperiments.treatmentOverrides,
    featureRolloutTreatmentsFromService: ecsFeatureRollouts.treatmentsFromService,
    featureRolloutTreatmentOverrides: ecsFeatureRollouts.treatmentOverrides,
  };
};

export const serializeLegacyEcsPersistedState = (snapshot: EcsSourceSnapshot): LegacyEcsPersistedFields => ({
  ecsConfig: JSON.stringify(snapshot.ecsConfig),
  ecsExperiments: JSON.stringify({
    treatments: {
      ...defaultExperimentTreatments,
      ...snapshot.experimentTreatmentsFromService,
      ...snapshot.experimentTreatmentOverrides,
    },
    treatmentsFromService: snapshot.experimentTreatmentsFromService,
    treatmentOverrides: snapshot.experimentTreatmentOverrides,
  } satisfies EcsExperiments),
  ecsFeatureRollouts: JSON.stringify({
    treatments: {
      ...defaultFeatureRolloutTreatments,
      ...snapshot.featureRolloutTreatmentsFromService,
      ...snapshot.featureRolloutTreatmentOverrides,
    },
    treatmentsFromService: snapshot.featureRolloutTreatmentsFromService,
    treatmentOverrides: snapshot.featureRolloutTreatmentOverrides,
  } satisfies EcsFeatureRollouts),
});
